// Interjections — the short bursts that carry emotion: reactions, fillers,
// and conversational sounds. Grouped reference lists sit at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const GROUPS = {
  "Reactions & emotion": [
    ["Ai!", "Ouch! / Oh no!"], ["Ufa!", "Phew! / Ugh!"], ["Epá!", "Wow! / Hey!"],
    ["Fixe!", "Great! / Cool!"], ["Qual quê!", "No way!"], ["Meu Deus!", "Oh my goodness!"],
    ["Caramba!", "Wow / Good grief!"], ["Oxalá!", "I hope so! / If only!"],
  ],
  "Agreement & encouragement": [
    ["Claro!", "Of course!"], ["Está bem!", "OK! / Alright!"], ["Vá lá!", "Come on!"],
    ["Bora!", "Come on, let's go!"], ["Isso mesmo!", "That's it!"], ["Força!", "Cheer up! / You can do it!"],
  ],
  "Fillers & hesitation": [
    ["Bom…", "Well…"], ["Então…", "Well… / So…"], ["Deixa ver…", "Let's see…"],
    ["Ou seja…", "I mean…"], ["Pronto…", "Right… / So…"], ["Sabes?", "You know?"],
  ],
  "Greetings & politeness": [
    ["Olá!", "Hi!"], ["Ouve! / Oiça!", "Hey! (listen)"], ["Saúde!", "Cheers! / Bless you!"],
    ["Socorro!", "Help!"], ["Atenção!", "Watch out!"], ["Basta!", "Enough!"],
  ],
};

export function interjectionsView(app) {
  const block = (title, rows) => `
    <div class="bg-white rounded-2xl border border-sage-100 p-5">
      <h3 class="text-lg font-bold text-sage-800 mb-3">${title}</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Portuguese</th><th class="py-2 px-3">English</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              ([pt, en]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${pt}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  const table = `
    <div class="grid sm:grid-cols-2 gap-4 mt-6">
      ${Object.entries(GROUPS)
        .map(([title, rows]) => block(title, rows))
        .join("")}
    </div>`;

  renderGuideSections(
    app,
    "Interjections",
    "Interjections are the short exclamations that express emotion or reaction — and the little fillers that keep a conversation flowing. They're grammatically independent, so they don't change form; you just learn them whole.",
    [
      {
        title: "1. How they work",
        body: `
          <p class="text-sage-700 mb-2">An interjection stands on its own — it isn't part of the sentence's grammar. Portuguese uses a normal exclamation mark, with <b>no</b> upside-down opener — just <b>…!</b>:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>Ai!</b> Magoei-me.</i> (Ouch! I've hurt myself.)</li>
            <li><i><b>Epá!</b> Não sabia.</i> (Wow! I didn't know.)</li>
          </ul>
        `,
      },
      {
        title: "2. Fillers buy you thinking time",
        body: `
          <p class="text-sage-700 mb-2">Natural speech is full of small fillers. Sprinkling these in makes you sound far more fluent than perfect grammar alone:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>Bom</b>, não sei…</i> (Well, I don't know…)</li>
            <li><i><b>Então</b>, a verdade é que…</i> (Well, the truth is…)</li>
            <li><i><b>Ou seja</b>, o que quero dizer é…</i> (I mean, what I want to say is…)</li>
            <li><i><b>Deixa ver</b>…</i> (Let's see…)</li>
          </ul>
        `,
      },
      {
        title: "3. Watch the register",
        body: `
          <p class="text-sage-700 text-sm">Many interjections are regional or informal. <i>Fixe</i> and <i>giro</i> are everywhere in Portugal; Brazil leans on <i>legal</i> and <i>beleza</i>. And some common exclamations are mild swears — fine among friends, risky in formal settings. When in doubt, the neutral picks are <i>Claro!</i>, <i>Boa!</i>, and <i>Caramba!</i>.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
